// ============================================
// PageContainer Component
// Provides consistent layout wrapper for all pages
// Standard: bg-[#FAF8FF], max-w-[1440px], px-8 py-6
// ============================================

import { cn } from '../../utils/cn';

// ============================================
// Types
// ============================================

interface PageContainerProps {
  /** Page content */
  children: React.ReactNode;
  /** Additional className for the outer wrapper */
  className?: string;
  /** Additional className for the inner container */
  containerClassName?: string;
  /** Use full width (no max-width constraint) */
  fullWidth?: boolean;
  /** Custom max width (default: 1440px) */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Custom background color (default: #FAF8FF) */
  background?: 'default' | 'white' | 'slate' | 'transparent';
  /** Custom padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

// ============================================
// Constants
// ============================================

const MAX_WIDTH_MAP = {
  sm: 'max-w-[1120px]',
  md: 'max-w-[1280px]',
  lg: 'max-w-[1440px]', // Default
  xl: 'max-w-[1600px]',
  '2xl': 'max-w-[1800px]',
  full: 'max-w-full',
} as const;

const BACKGROUND_MAP = {
  default: 'bg-[#FAF8FF]',
  white: 'bg-white',
  slate: 'bg-slate-50',
  transparent: 'bg-transparent',
} as const;

const PADDING_MAP = {
  none: '',
  sm: 'px-4 py-4 lg:px-6',
  md: 'px-6 py-6 lg:px-8',
  lg: 'px-8 py-6', // Default
} as const;

// ============================================
// Component
// ============================================

export function PageContainer({
  children,
  className,
  containerClassName,
  fullWidth = false,
  maxWidth = 'lg',
  background = 'default',
  padding = 'lg',
}: PageContainerProps) {
  return (
    <div
      className={cn(
        'min-h-screen',
        BACKGROUND_MAP[background],
        className
      )}
    >
      <div
        className={cn(
          'mx-auto w-full',
          !fullWidth && MAX_WIDTH_MAP[maxWidth],
          PADDING_MAP[padding],
          containerClassName
        )}
      >
        {children}
      </div>
    </div>
  );
}

// ============================================
// Variant for Auth pages (centered, no sidebar)
// ============================================

interface AuthPageContainerProps {
  children: React.ReactNode;
  className?: string;
}

export function AuthPageContainer({ children, className }: AuthPageContainerProps) {
  return (
    <div
      className={cn(
        'flex min-h-screen items-center justify-center',
        'bg-gradient-to-br from-slate-100 to-slate-200',
        'px-4 py-8',
        className
      )}
    >
      {children}
    </div>
  );
}
